import React, { useState, useEffect } from 'react';
import {
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Alert,
  Paper,
  Divider,
  InputAdornment
} from '@mui/material';
import { LocationOn, Store as StoreIcon, Phone } from '@mui/icons-material';
import { useApp } from '../../context/AppContext';
import api from '../../utils/api';
import GoogleMapComponent from '../../components/common/GoogleMap';

const AddStore = () => {
  const { actions } = useApp();
  const [loading, setLoading] = useState(false);
  const [stores, setStores] = useState([]);
  const [formData, setFormData] = useState({
    storeName: '',
    location: '',
    branchId: '',
    locCode: '',
    address: '',
    pincode: '',
    contactNo: '',
    deliveryRadius: 5000,
    status: true,
    selfPickup: false,
    restrictCOD: false,
    isDarkStore: false,
    mainStoreId: ''
  });

  useEffect(() => {
    fetchStores();
  }, []);

  const fetchStores = async () => {
    try {
      const response = await api.get('/stores');
      // Filter only regular stores (not dark stores)
      const regularStores = response.data.stores?.filter(store => !store.isDarkStore) || [];
      console.log('Available stores for main store selection:', regularStores);
      setStores(regularStores);
    } catch (error) {
      console.error('Error fetching stores:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleMapClick = (lat, lng) => {
    // Handle map click to set coordinates
    setFormData(prev => ({
      ...prev,
      location: `${lat.toFixed(6)}, ${lng.toFixed(6)}`
    }));
    actions.showSnackbar('Location selected on map', 'success');
  };

  const validateForm = () => {
    const errors = [];
    
    if (!formData.storeName.trim()) errors.push('Store name is required');
    if (!formData.branchId.trim()) errors.push('Branch ID is required');
    if (!formData.location.trim()) errors.push('Location coordinates are required');
    if (!formData.locCode.trim()) errors.push('Location code is required');
    if (formData.isDarkStore && !formData.mainStoreId) {
      errors.push('Main store selection is required for dark stores');
    }
    if (!formData.address.trim()) errors.push('Address is required');
    if (!formData.pincode || formData.pincode.length !== 6) errors.push('Valid 6-digit pincode is required');
    if (!formData.contactNo || !/^[6-9]\d{9}$/.test(formData.contactNo)) {
      errors.push('Valid Indian mobile number is required');
    }
    if (!formData.deliveryRadius || formData.deliveryRadius <= 0) {
      errors.push('Delivery radius must be greater than 0');
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateForm();
    if (errors.length > 0) {
      actions.showSnackbar(errors[0], 'error');
      return;
    }

    setLoading(true);
    try {
      console.log('Sending store data:', formData);
      await api.post('/stores', formData);
      actions.showSnackbar('Store added successfully!', 'success');
      
      // Reset form
      setFormData({
        storeName: '',
        location: '',
        branchId: '',
        locCode: '',
        address: '',
        pincode: '',
        contactNo: '',
        deliveryRadius: 5000,
        status: true,
        selfPickup: false,
        restrictCOD: false,
        isDarkStore: false,
        mainStoreId: ''
      });
      
    } catch (error) {
      actions.showSnackbar(error.response?.data?.message || 'Error adding store', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={2} sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <StoreIcon sx={{ mr: 2, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Add New Store
          </Typography>
        </Box>

        <Divider sx={{ mb: 4 }} />

        <form onSubmit={handleSubmit}>
          <Box sx={{ maxWidth: 600, mx: 'auto' }}>
            {/* Store Basic Information */}
            <Typography variant="h6" sx={{ mb: 3, color: 'primary.main' }}>
              Store Information
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 4 }}>
              <TextField
                fullWidth
                label="Store Name"
                name="storeName"
                value={formData.storeName}
                onChange={handleInputChange}
                required
                variant="outlined"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isDarkStore}
                    onChange={handleInputChange}
                    name="isDarkStore"
                    color="primary"
                  />
                }
                label="Dark Store"
              />

              {formData.isDarkStore && (
                <FormControl fullWidth required>
                  <InputLabel>Main Store</InputLabel>
                  <Select
                    name="mainStoreId"
                    value={formData.mainStoreId}
                    label="Main Store"
                    onChange={handleInputChange}
                  >
                    {stores.map((store) => (
                      <MenuItem key={store.id} value={store.branchId}>
                        {store.storeName} ({store.branchId})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <TextField
                fullWidth
                label="Branch ID"
                name="branchId"
                value={formData.branchId}
                onChange={handleInputChange}
                required
                variant="outlined"
                helperText="Enter a unique identifier for this store"
              />
            </Box>

            {/* Location Information */}
            <Typography variant="h6" sx={{ mb: 3, color: 'primary.main' }}>
              Location Details
            </Typography>

            {/* Google Maps Integration */}
            <Box sx={{ mb: 3 }}>
                          <GoogleMapComponent
              onLocationSelect={handleMapClick}
              selectedLocation={
                formData.location
                  ? { 
                      lat: parseFloat(formData.location.split(',')[0]?.trim()),
                      lng: parseFloat(formData.location.split(',')[1]?.trim())
                    }
                  : null
              }
            />
              
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 2 }}>
                <strong>How to use:</strong> 
                1. Search for a location using the search box above the map, OR
                2. Click directly on the map to set the store location.
                The coordinates will be automatically filled in the form fields below.
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 4 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Location (lat, lng)"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  required
                  placeholder="17.449800, 78.398870"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOn />
                      </InputAdornment>
                    ),
                  }}
                  helperText="Format: latitude, longitude"
                />
                <TextField
                  fullWidth
                  label="Location Code"
                  name="locCode"
                  value={formData.locCode}
                  onChange={handleInputChange}
                  required
                  placeholder="Z5LYI2KS4C"
                  helperText="Unique location identifier"
                />
              </Box>

              <TextField
                fullWidth
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                required
                multiline
                rows={3}
                variant="outlined"
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Pincode"
                  name="pincode"
                  value={formData.pincode}
                  onChange={handleInputChange}
                  required
                  inputProps={{ maxLength: 6, pattern: '[0-9]{6}' }}
                  helperText="6-digit pincode"
                />
                <TextField
                  fullWidth
                  label="Contact Number"
                  name="contactNo"
                  value={formData.contactNo}
                  onChange={handleInputChange}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Phone />
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{ maxLength: 10, pattern: '[6-9][0-9]{9}' }}
                  helperText="10-digit Indian mobile number"
                />
              </Box>
            </Box>

            {/* Store Settings */}
            <Typography variant="h6" sx={{ mb: 3, color: 'primary.main' }}>
              Store Settings
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 4 }}>
              <TextField
                fullWidth
                label="Delivery Radius (meters)"
                name="deliveryRadius"
                value={formData.deliveryRadius}
                onChange={handleInputChange}
                required
                type="number"
                inputProps={{ min: 100, max: 50000 }}
                helperText="Enter delivery radius in meters"
              />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.status}
                      onChange={handleInputChange}
                      name="status"
                      color="primary"
                    />
                  }
                  label="Active Status"
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.selfPickup}
                      onChange={handleInputChange}
                      name="selfPickup"
                      color="primary"
                    />
                  }
                  label="Enable Self Pickup"
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.restrictCOD}
                      onChange={handleInputChange}
                      name="restrictCOD"
                      color="primary"
                    />
                  }
                  label="Restrict Cash on Delivery"
                />
              </Box>
            </Box>

            {/* Submit Button */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                sx={{ minWidth: 200, py: 1.5 }}
              >
                {loading ? 'Adding Store...' : 'Add Store'}
              </Button>
            </Box>
          </Box>
        </form>

      </Paper>
    </Box>
  );
};

export default AddStore; 